import { mainApi } from '../config/apiClient';

// ─────────────────────────────────────────────────────────────────────────────
// Tipler
// ─────────────────────────────────────────────────────────────────────────────

export interface TicketReservationLineItem {
  urunId?: string;
  urunAdi?: string;
  birimFiyat?: number;
  miktar?: number;
  toplamFiyat?: number;
  urunResmi?: string | null;
  aciklama?: string | null;
  categoryId?: string | null;
}

export interface TicketReservationGuest {
  firstName?: string;
  lastName?: string;
  day?: string | number;
  month?: string | number;
  year?: string | number;
  age?: number;
  type?: string;
}

export interface TicketReservationRecord {
  id: number;
  /** ReservationId (örn. TR202604281452357571 — PNR olarak da kullanılır) */
  siparisId: string;
  kullaniciAdi: string;
  toplamTutar: number;
  /** Backend'in normalize ettiği siparis durumu (Onaylandı/Beklemede/Reddedildi/İptal) */
  siparisDurumu: string;
  siparisOlusturmaTarihi: string;
  siparisGuncellenmeTarihi?: string;
  branchId?: string;
  /** ReservationDate — yyyy-MM-dd format */
  reservationDate?: string;
  guestCount?: number;
  authorizedPerson?: {
    fullName?: string;
    phone?: string;
    email?: string;
    identityNumber?: string;
    address?: string;
  };
  guests?: TicketReservationGuest[];
  lineItems?: TicketReservationLineItem[];
  odemeBasarili?: boolean;
  odemeDurumu?: string;
}

interface TicketReservationListResponse {
  success: boolean;
  data: TicketReservationRecord[];
  totalCount: number;
  page?: number;
  pageSize?: number;
}

export interface GetTicketReservationsOptions {
  branchId?: string | null;
  status?: string | null;
  search?: string | null;
  /** YYYY-MM-DD formatında tarih (gün başı kabul edilir) */
  fromDate?: string | null;
  /** YYYY-MM-DD formatında tarih (gün sonuna kadar inclusive) */
  toDate?: string | null;
  page?: number;
  pageSize?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// API Çağrıları
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ticket rezervasyonlarını esnek filtre seçenekleriyle çeker.
 * branchId/status/search/fromDate/toDate hepsi opsiyonel.
 *
 * Multi-tenant: createApiClient otomatik X-Company-Id header'ı ekler;
 * sadece login olan firma kullanıcısının kayıtları döner.
 */
export const getTicketReservations = async (
  opts: GetTicketReservationsOptions = {}
): Promise<TicketReservationRecord[]> => {
  const params: Record<string, string | number> = {
    page: opts.page ?? 1,
    pageSize: opts.pageSize ?? 100,
  };
  if (opts.branchId) params.branchId = opts.branchId;
  if (opts.status) params.status = opts.status;
  if (opts.search) params.search = opts.search;
  if (opts.fromDate) params.fromDate = opts.fromDate;
  if (opts.toDate) params.toDate = opts.toDate;

  const { data } = await mainApi.get<TicketReservationListResponse>(
    '/api/TicketReservation',
    { params }
  );
  if (!data?.success) {
    return [];
  }
  return data.data || [];
};

/**
 * Geriye dönük uyum: branchId-only çağrılar için.
 * Yeni kod `getTicketReservations({ branchId, ... })` kullanmalı.
 */
export const getTicketReservationsByBranch = async (
  branchId: string,
  page = 1,
  pageSize = 100
): Promise<TicketReservationRecord[]> => {
  return getTicketReservations({ branchId, page, pageSize });
};

export const updateTicketReservationStatus = async (
  reservationId: number,
  status: 'Onaylandı' | 'Reddedildi' | 'Rezervasyon Beklemede'
): Promise<void> => {
  await mainApi.post(`/api/TicketReservation/${reservationId}/status`, {
    SiparisDurumu: status,
  });
};
